import { VuexModule, Module, Action, Mutation, getModule } from 'vuex-module-decorators'
import axios, { CancelTokenSource } from 'axios'
import store from '@/store'
import { UploadFileRes } from '@/api/common/common-api-type'
import { downloadOfflineAsrURL, getOfflineAsrProgressApi, startOfflineAsrTaskApi } from '@/api/asr'
import { nextTick } from 'vue'
import { Message } from 'element-ui'

export interface IAsrOfflineTask {
  recordId: string
  lang: 'cn' | 'eng'
  property: string
  fileList: UploadFileRes[]
}

type StatusType = 'init' | 'startLoading' | 'going'

export interface IAsrOfflineTaskState {
  task: IAsrOfflineTask | null
  progress: number
  status: StatusType
}

@Module({ dynamic: true, store, name: 'asr-offline' })
class AsrOffline extends VuexModule implements IAsrOfflineTaskState {
  public task: IAsrOfflineTask | null = null
  public progress = 0
  public status: StatusType = 'init'

  private timer: number | undefined
  private cancelToken: CancelTokenSource | undefined

  @Mutation
  private SET_TASK (task: IAsrOfflineTask | null) {
    this.task = task
  }

  @Mutation
  private SET_PROGRESS (progress: number) {
    this.progress = progress
  }

  @Mutation
  private SET_STATUS (status: StatusType) {
    this.status = status
  }

  @Action
  public START_TASK (params: Pick<IAsrOfflineTask, "fileList" | "property" | "lang">) {
    this.SET_STATUS('startLoading')
    startOfflineAsrTaskApi(params.property, params.fileList.map(i => i.id)).then(({ data: recordId }) => {
      this.SET_TASK({ recordId, ...params })
      this.SET_STATUS('going')
      this.SET_PROGRESS(0)

      this._loopCheckProgress()
    }).catch(err => {
      this.SET_STATUS('init')
      throw err
    })
  }

  @Action
  public CLEAR_TASK() {
    this.SET_TASK(null)
    this.SET_PROGRESS(0)
    this.SET_STATUS('init')
    this.cancelToken?.cancel()
    this.cancelToken = undefined
    clearInterval(this.timer)
    this.timer = undefined
  }

  @Action
  // 休眠任务
  public HIBERNATE_TASK() {
    if(this.task) {
      clearInterval(this.timer)
      this.timer = undefined
    }
  }

  @Action
  // 唤醒任务
  public AWAKEN_TASK() {
    if(this.task) {
      this._loopCheckProgress()
    }
  }

  @Action
  private _loopCheckProgress() {
    this.timer = window.setInterval(() => {
      if(!this.task) {
        this.CLEAR_TASK()
        return
      }
      this.cancelToken?.cancel()
      this.cancelToken = axios.CancelToken.source()
      getOfflineAsrProgressApi(
        this.task.recordId,
        { cancelToken: this.cancelToken.token }
      ).then(({ data }) => {
        this.SET_PROGRESS(data.progress)
        if(data.progress >= 100) {
          Message(`音视频识别成功${data.successNum ?? 0}个，失败${data.failNum ?? 0}个`)
          if(data.successNum && data.successNum > 0) {
            this._downloadTaskResult()
          } else {
            this.CLEAR_TASK()
          }
        }
      }).catch((err) => {
        if(!(axios.isCancel(err) || err?.errorType === 'cancel')) {
          // 如果不是取消请求就清除任务
          this.CLEAR_TASK()
        }
      })
    }, 2000)
  }

  @Action
  private _downloadTaskResult() {
    if(!this.task) return
    const url = downloadOfflineAsrURL(this.task.recordId)
    const a = document.createElement('a')
    a.href = url
    a.download = url
    a.click()
    nextTick(() => {
      a.remove()
      this.CLEAR_TASK()
    })
  }
}

export const AsrOfflineModule = getModule(AsrOffline)
